<template>
  <div>
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        class="promocode-campaigns-tickets-modal !w-[760px] !max-w-[760px] !max-h-[85vh] !p-0 !gap-0 !grid-rows-[auto_1fr_auto]"
        data-cy="campaign-tickets-modal"
        @open-auto-focus.prevent
      >
        <SDialogHeader
          class="space-y-4 border-b border-[#e8e8e8] px-6 pb-5 pt-6 text-left"
        >
          <SDialogTitle class="text-lg font-normal text-[#202223]">
            {{ t("promo-campaigns.select-items") }}
            <span class="font-semibold">
              {{ promocodeData?.name }}
              <template v-if="promocodeDiscountDisplay">
                {{ promocodeDiscountDisplay }}
              </template>
            </span>
          </SDialogTitle>
          <div class="search-container">
            <input
              class="search-input"
              type="search"
              :placeholder="t('input.promocode-campaigns') + '...'"
              v-model="searchQuery"
            />
          </div>
        </SDialogHeader>

        <div class="ticket-list-wrapper">
          <template v-if="displayedLines.length">
            <div
              v-for="(row, idx) in displayedLines"
              :key="row.index"
              class="ticket-row"
              :class="{ 'ticket-row--last': idx === displayedLines.length - 1 }"
              data-cy="campaign-ticket-row"
            >
              <div class="ticket-name">
                <div class="ticket-name-row">
                  <span class="ticket-category">{{ row.categoryName }}</span>
                  <span class="ticket-sep">|</span>
                  <span class="ticket-type">
                    {{
                      row.isRetail
                        ? t("promo-campaigns.retail")
                        : t("promo-campaigns.ticket")
                    }}
                  </span>
                  <span class="ticket-sep">|</span>
                  <span class="ticket-price">{{ row.priceName }}</span>
                </div>
                <div
                  v-if="getOrderLineAppliedCodes(row.orderLineId).length"
                  class="pss-discounts"
                >
                  <p
                    class="pss-applied-discounts"
                    v-for="line of getOrderLineAppliedCodes(row.orderLineId)"
                    :key="line.code"
                  >
                    <SvgIcon
                      :iconContent="CouponIcon"
                      iconAlt=""
                      iconStyle="width: 14px; height: 14px"
                    />
                    {{ line.code }}
                    <span class="font-bold">({{ line.percentage }}%)</span>
                  </p>
                </div>
                <div
                  v-if="!row.isRetail && row.startDateTime"
                  class="pss-timeslot"
                >
                  {{ formatDateTime(String(row.startDateTime)) }}
                </div>
              </div>
              <div class="ticket-row-right">
                <div class="ticket-qty">
                  <button
                    type="button"
                    class="qty-button"
                    @click="decrementLineCount(row.index)"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    class="qty-input"
                    v-model.number="lineCount[row.index]"
                  />
                  <button
                    type="button"
                    class="qty-button"
                    :disabled="lineCount[row.index] === row.quantity"
                    @click="incrementLineCount(row.index, row.quantity)"
                  >
                    +
                  </button>
                </div>
                <SButton
                  type="button"
                  size="sm"
                  class="max-btn"
                  :disabled="lineCount[row.index] === row.quantity"
                  @click="setLineCount(row.index, row.quantity)"
                >
                  Max (x{{ row.quantity }})
                </SButton>
              </div>
            </div>
          </template>
          <div v-else class="empty-state">
            <p>{{ t("promo-campaigns.no-matching-tickets") }}</p>
          </div>
        </div>

        <SDialogFooter
          class="!flex-row !justify-between !items-center border-t border-[#e8e8e8] px-6 py-4 gap-3"
        >
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ t("button.cancel") }}
          </SButton>
          <div class="flex gap-3">
            <SButton
              type="button"
              variant="outline"
              class="min-w-[180px]"
              :loading="isapplyToAllLoading"
              :disabled="isapplyToSomeLoading"
              @click="applyToAllTickets"
              data-cy="apply-to-all-btn"
            >
              {{ t("button.apply-to-all") }}
            </SButton>
            <SButton
              type="button"
              class="min-w-[140px]"
              :loading="isapplyToSomeLoading"
              :disabled="isapplyToAllLoading"
              @click="applyToSelectedTickets"
            >
              {{ t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import {
  Discount,
  DiscountType,
  LineType,
  OrderDiscount,
  TargetQuantity,
} from "@smeetz/order-common";
import { OrderLinePublicType } from "@smeetz/orders";
import { useOrderStore } from "@smeetz/order-store";
import { IPromocodeCardData } from "@/types/db/promocode-campaign";
import NotificationService from "@/services/notification.service";
import { formatDateTime } from "@/utils/helpers/dates";
import CouponIcon from "@/assets/images/svg/coupon.svg";
import { applyDiscountCode } from "@/utils/vue-helpers/orders.helper";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

interface DisplayedLine {
  index: number;
  orderLineId: string;
  categoryName: string;
  priceName: string;
  quantity: number;
  isRetail: boolean;
  startDateTime?: Date;
}

export default defineComponent({
  name: ModalComponents.PromocodeCampaignsTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
  },
  props: {
    modalProps: {
      type: Object as PropType<{
        referrer?: ModalComponents;
        goBack?: boolean;
        props?: IPromocodeCardData;
      }>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const orderStore = useOrderStore();
    const modalVisibility = dialogVisibleForStatus(
      ModalStatus.PromocodeCampaignsTickets
    );
    const searchQuery = ref("");
    const isapplyToAllLoading = ref(false);
    const isapplyToSomeLoading = ref(false);
    const lineCount = ref<Record<number, number>>({});
    const promocodeData = computed(() => {
      return props.modalProps.props;
    });

    const orderSession = computed(() => orderStore.selectedOrder?.orderSession);

    const promocodeDiscountDisplay = computed(() => {
      const data = promocodeData.value;
      if (!data) return "";
      if (data.percentageValue && data.percentageValue > 0) {
        return `${data.percentageValue}%`;
      }
      if (data.flatValue && data.flatValue > 0) {
        return `${data.flatValue}`;
      }
      return "";
    });

    const displayedLines = computed<DisplayedLine[]>(() => {
      const orderLines = orderSession.value?.orderLines ?? [];
      const query = searchQuery.value.toLowerCase();

      return orderLines
        .filter((line) => line.lineType === LineType.SaleLine)
        .filter((line) => {
          if (!query) return true;
          const cat = String(line.categoryName ?? "").toLowerCase();
          const prc = String(line.priceName ?? "").toLowerCase();
          return cat.includes(query) || prc.includes(query);
        })
        .map((line, index) => ({
          index,
          orderLineId: line.id,
          categoryName: String(line.categoryName ?? ""),
          priceName: String(line.priceName ?? ""),
          quantity: Number(line.quantity ?? 0),
          isRetail: line.itemType === OrderLinePublicType.RETAIL,
          startDateTime: line.startDateTime,
        }));
    });

    function incrementLineCount(index: number, max: number) {
      const current = lineCount.value[index] ?? 0;
      if (current < max) {
        lineCount.value[index] = current + 1;
      }
    }

    function decrementLineCount(index: number) {
      const current = lineCount.value[index] ?? 0;
      if (current === 0) return;
      lineCount.value[index] = current - 1;
    }

    function setLineCount(index: number, qty: number) {
      lineCount.value[index] = qty;
    }

    /**
     * Apply the code only to the user-selected lines/quantities.
     * Builds `targetOrderLineIds` and `targetQuantities` from `lineCount` so
     * the package allocates the discount only to those units.
     */
    async function applyToSelectedTickets() {
      if (!promocodeData.value) return;

      const hasSelected = Object.values(lineCount.value).some((n) => n > 0);
      if (!hasSelected) {
        NotificationService.showCustomInfoWithTitle(
          t("promo-campaigns.select-item-to-apply"),
          "Info"
        );
        return;
      }

      const targetQuantities: TargetQuantity[] = [];
      for (const row of displayedLines.value) {
        const qty = lineCount.value[row.index] ?? 0;
        if (qty <= 0) continue;
        targetQuantities.push({ orderLineId: row.orderLineId, quantity: qty });
      }

      isapplyToSomeLoading.value = true;
      try {
        await applyDiscountCode(
          promocodeData.value.code,
          undefined,
          targetQuantities
        );
      } catch {
        // journaled + surfaced by the order helper; the code is not applied
      } finally {
        isapplyToSomeLoading.value = false;
        hideDialog();
      }
    }

    /**
     * Apply the code across the whole order — let the package allocate per the
     * code's `useOnePer` rule.
     */
    async function applyToAllTickets() {
      if (!promocodeData.value) return;
      isapplyToAllLoading.value = true;
      try {
        await applyDiscountCode(promocodeData.value.code);
      } catch {
        // journaled + surfaced by the order helper; the code is not applied
      } finally {
        isapplyToAllLoading.value = false;
        hideDialog();
      }
    }

    onMounted(() => {
      const sessionDiscounts: Discount[] = orderSession.value?.discounts ?? [];
      const orderDiscounts: OrderDiscount[] =
        orderSession.value?.orderDiscounts ?? [];

      const codeOrderDiscountIds = new Set(
        orderDiscounts
          .filter(
            (od) =>
              od.type === DiscountType.PROMOCODE &&
              String(od.code) === promocodeData.value?.code
          )
          .map((od) => od.id)
      );

      for (const row of displayedLines.value) {
        const appliedQty = sessionDiscounts
          .filter(
            (d) =>
              d.lineId === row.orderLineId &&
              codeOrderDiscountIds.has(d.orderDiscountId)
          )
          .reduce((sum, d) => sum + Number(d.quantityApplied || 0), 0);
        lineCount.value[row.index] = appliedQty;
      }
    });

    function getOrderLineAppliedCodes(
      orderLineId: string
    ): { code: string; percentage: string }[] {
      const sessionDiscounts: Discount[] = orderSession.value?.discounts ?? [];
      const orderDiscounts: OrderDiscount[] =
        orderSession.value?.orderDiscounts ?? [];

      const codeOrderDiscounts = orderDiscounts.filter(
        (od) => od.type === DiscountType.PROMOCODE && od.code
      );
      if (!codeOrderDiscounts.length) return [];

      const lines: { code: string; percentage: string }[] = [];
      for (const od of codeOrderDiscounts) {
        const relatedDiscounts = sessionDiscounts.filter(
          (d) => d.orderDiscountId === od.id && d.lineId === orderLineId
        );
        if (!relatedDiscounts.length) continue;

        const percentage = od.percentDiscount
          ? String(Math.round(od.percentDiscount * 100))
          : "";
        lines.push({ code: String(od.code), percentage });
      }
      return lines;
    }

    return {
      t,
      modalVisibility,
      searchQuery,
      isapplyToAllLoading,
      isapplyToSomeLoading,
      hideDialog,
      displayedLines,
      lineCount,
      incrementLineCount,
      decrementLineCount,
      setLineCount,
      applyToSelectedTickets,
      applyToAllTickets,
      promocodeData,
      promocodeDiscountDisplay,
      formatDateTime,
      getOrderLineAppliedCodes,
      CouponIcon,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.promocode-campaigns-tickets-modal {
  .search-container {
    position: relative;
  }

  .search-input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    transition: border-color 0.15s ease;

    &::placeholder {
      color: #b0b0b0;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-smeetz);
      box-shadow: 0 0 0 3px rgba(var(--primary-smeetz-rgb, 0, 161, 158), 0.12);
    }
  }

  .ticket-list-wrapper {
    overflow-x: hidden;
    overflow-y: auto;
    padding: 8px 24px 16px;
    min-width: 0;
  }

  .empty-state {
    height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8a8a8e;
    font-size: 14px;
  }

  .ticket-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 14px 0;
    border-bottom: 1px solid #ececec;
    min-width: 0;

    &.ticket-row--last {
      border-bottom: none;
    }
  }

  .ticket-name {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
  }

  .ticket-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    color: #202223;
    font-size: 14px;
    line-height: 1.4;
  }

  .ticket-category {
    font-weight: 700;
  }

  .ticket-sep {
    color: #c4c4c4;
  }

  .pss-discounts {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .pss-applied-discounts {
    margin: 0;
    color: $color-green-eagle;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    line-height: 1;
  }

  .pss-timeslot {
    font-size: 12px;
    color: #8a8a8e;
  }

  .ticket-row-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .ticket-qty {
    display: flex;
    align-items: stretch;
  }

  .qty-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    width: 34px;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    color: #333333;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    font-weight: 500;
    user-select: none;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:first-child {
      border-radius: 6px 0 0 6px;
      border-right: none;
    }

    &:last-child {
      border-radius: 0 6px 6px 0;
      border-left: none;
    }
  }

  .qty-input {
    height: 34px;
    width: 56px;
    text-align: center;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    border-left: none;
    border-right: none;
    font-size: 14px;
    font-weight: 600;
    color: #202223;
    margin: 0;
    outline: none;
    border-radius: 0;
  }

  .qty-input::-webkit-inner-spin-button,
  .qty-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .qty-input[type="number"] {
    -moz-appearance: textfield;
  }

  .max-btn {
    min-width: 86px;
  }
}
</style>
